/**
 * State and handlers for CreatableCombobox.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  displayForCommittedValue,
  filterLabeledOptions,
  filterPlainOptions,
  labeledExactMatch,
  normalizeLabeledOptions,
  normalizePlainOptions,
  safeStr,
  type LabeledComboboxOption,
} from './creatable-combobox.helpers';

function dismissEventTarget(ev: unknown): Node | null {
  if (!ev || typeof ev !== 'object') return null;
  const e = ev as {
    detail?: { originalEvent?: Event };
    target?: EventTarget | null;
  };
  const orig = e.detail?.originalEvent;
  const raw = orig?.target ?? e.target ?? null;
  return raw instanceof Node ? raw : null;
}

interface Params {
  options: string[];
  labeledOptions?: LabeledComboboxOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  /** Sort options alphabetically by label (default); false preserves input order. */
  sortOptions?: boolean;
}

export function useCreatableCombobox({
  options,
  labeledOptions,
  value,
  onChange,
  sortOptions = true,
}: Params) {
  const labeledMode = labeledOptions !== undefined;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => String(value ?? ''));
  const committedRef = useRef(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const uniquePlain = useMemo(
    () => (labeledMode ? [] : normalizePlainOptions(options, sortOptions)),
    [labeledMode, options, sortOptions],
  );
  const uniqueLabeled = useMemo(
    () => (labeledMode ? normalizeLabeledOptions(labeledOptions!, sortOptions) : []),
    [labeledMode, labeledOptions, sortOptions],
  );

  // While the input still shows the committed value (nothing typed yet), show
  // the FULL option list so opening the dropdown reveals every choice; once the
  // user types a different query, filter by it. Without this, editing a field
  // pre-filled with a value would only ever list that one value.
  const committedDisplay = labeledMode
    ? displayForCommittedValue(safeStr(value), uniqueLabeled)
    : safeStr(value);
  const showAll = safeStr(inputValue) === committedDisplay;
  const filteredPlain = useMemo(
    () => (showAll ? uniquePlain : filterPlainOptions(uniquePlain, inputValue)),
    [showAll, uniquePlain, inputValue],
  );
  const filteredLabeled = useMemo(
    () => (showAll ? uniqueLabeled : filterLabeledOptions(uniqueLabeled, inputValue)),
    [showAll, uniqueLabeled, inputValue],
  );

  const exactMatch = labeledMode
    ? labeledExactMatch(uniqueLabeled, inputValue)
    : uniquePlain.some((o) => o.toLowerCase() === safeStr(inputValue).trim().toLowerCase());

  const canCreate = safeStr(inputValue).trim().length > 0 && !exactMatch;
  const presetCount = labeledMode ? uniqueLabeled.length : uniquePlain.length;

  const keepOpenIfInsideAnchor = useCallback((event: { preventDefault: () => void }) => {
    const node = anchorRef.current;
    const target = dismissEventTarget(event);
    if (node && target && node.contains(target)) event.preventDefault();
  }, []);

  const commitDisplay = useCallback(
    (v: string) =>
      labeledMode ? displayForCommittedValue(v, uniqueLabeled) : v,
    [labeledMode, uniqueLabeled],
  );

  const handleSelect = useCallback(
    (selected: string) => {
      committedRef.current = true;
      onChange(selected);
      setInputValue(commitDisplay(selected));
      setOpen(false);
    },
    [commitDisplay, onChange],
  );

  const handleCreate = useCallback(() => {
    const newVal = safeStr(inputValue).trim();
    if (!newVal) return;
    committedRef.current = true;
    onChange(newVal);
    setInputValue(commitDisplay(newVal));
    setOpen(false);
  }, [commitDisplay, inputValue, onChange]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        if (!committedRef.current) {
          const trimmed = safeStr(inputValue).trim();
          onChange(trimmed);
          setInputValue(commitDisplay(trimmed));
        }
        committedRef.current = false;
      }
      setOpen(next);
    },
    [commitDisplay, inputValue, onChange],
  );

  useEffect(() => {
    setInputValue(displayForCommittedValue(value ?? '', labeledMode ? uniqueLabeled : undefined));
  }, [labeledMode, uniqueLabeled, value]);

  return {
    open,
    setOpen,
    inputValue,
    setInputValue,
    anchorRef,
    keepOpenIfInsideAnchor,
    labeledMode,
    filteredPlain,
    filteredLabeled,
    presetCount,
    canCreate,
    handleSelect,
    handleCreate,
    handleOpenChange,
  };
}
